import { createClient } from '@supabase/supabase-js';
import { config } from '../utils/config';

// Supabase configuration using smart detection from config.ts
const supabaseUrl = config.SUPABASE_URL;
const supabaseAnonKey = config.SUPABASE_ANON_KEY;

// Check if the configuration is valid
if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error("Supabase URL and Anon Key must be configured in config.ts");
}

// Robust storage adapter for Supabase that handles storage failures gracefully
const createRobustStorage = () => {
  // Remove duplicate mobile detection - use the centralized useMobileDetection hook instead
  
  return {
    getItem: (key: string) => {
      try {
        // Try localStorage first
        const item = globalThis.localStorage.getItem(key);
        if (item) return item;
        
        // Fallback to sessionStorage if localStorage fails
        return globalThis.sessionStorage.getItem(key);
      } catch (error) {
        console.warn('📱 Storage getItem failed:', error);
        return null;
      }
    },
    setItem: (key: string, value: string) => {
      try {
        // Try localStorage first
        globalThis.localStorage.setItem(key, value);
        
        // Also set in sessionStorage as backup for reliability
        globalThis.sessionStorage.setItem(key, value);
      } catch (error) {
        console.warn('📱 Storage setItem failed, trying sessionStorage:', error);
        try {
          globalThis.sessionStorage.setItem(key, value);
        } catch (sessionError) {
          console.error('📱 Both localStorage and sessionStorage failed:', sessionError);
        }
      }
    },
    removeItem: (key: string) => {
      try {
        globalThis.localStorage.removeItem(key);
        globalThis.sessionStorage.removeItem(key);
      } catch (error) {
        console.warn('📱 Storage removeItem failed:', error);
        try {
          globalThis.sessionStorage.removeItem(key);
        } catch (sessionError) {
          console.error('📱 Both localStorage and sessionStorage removeItem failed:', sessionError);
        }
      }
    }
  };
};

// Create Supabase client with mobile-optimized configuration
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true, // Required for OAuth flows to work properly
    flowType: 'pkce', // More secure authentication flow
    storage: createRobustStorage(), // 🔧 Robust storage with fallbacks
    debug: process.env.NODE_ENV === 'development' // Enable auth debugging in dev
  },
  global: {
    headers: {
      'Content-Type': 'application/json'
    }
  }
});
